import { useEffect, useState } from "react";
import { ShieldAlert, MessageCircle, Info, Loader2, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConsultingRequestButton } from "@/components/ConsultingRequestButton";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CHECKPOINT_TAB } from "@/constants/testIds";

const FINIQUITO_LABELS = [
  ["aguinaldo_proporcional", "Aguinaldo proporcional"],
  ["vacaciones_no_tomadas", "Vacaciones acumuladas sin tomar"],
];

const INDEMNIZACION_LABELS = [
  ["indemnizacion_constitucional", "Indemnización constitucional"],
  ["veinte_dias_por_anio", "20 días por año de antigüedad"],
  ["prima_antiguedad", "Prima de antigüedad"],
];

export function CheckpointTab({ employeeId, onGoToSalary }) {
  const [checkpoint, setCheckpoint] = useState(null);
  const [error, setError] = useState(null);
  const [causa, setCausa] = useState("");

  useEffect(() => {
    setCheckpoint(null);
    const query = causa ? `?causa=${encodeURIComponent(causa)}` : "";
    api.get(`/employees/${employeeId}/checkpoint${query}`)
      .then(({ data }) => setCheckpoint(data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [employeeId, causa]);

  if (error) {
    return (
      <div data-testid={CHECKPOINT_TAB.missingSalaryNotice} className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
        <p className="text-charcoal">{error}</p>
        <Button onClick={onGoToSalary} className="mt-3 bg-petroleum rounded-full">Ir a Salario</Button>
      </div>
    );
  }

  if (!checkpoint) {
    return (
      <div data-testid={CHECKPOINT_TAB.loadingIndicator} className="mt-8 flex flex-col items-center justify-center gap-2 text-charcoal/50 py-10">
        <Loader2 className="w-6 h-6 animate-spin text-petroleum" />
        <p className="text-sm">Calculando estimado de costo y guía de conversación...</p>
      </div>
    );
  }

  const l = checkpoint.liquidation_estimate;

  return (
    <div className="mt-4 space-y-4">
      <div className="bg-card border border-petroleum/10 rounded-2xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm text-charcoal/60">Sueldo cargado con prestaciones de ley (estimado)</p>
          <span data-testid={CHECKPOINT_TAB.pendingReviewBadge} className="text-xs font-medium text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">Pendiente de validación por contador o abogado laboral</span>
        </div>
        <p data-testid={CHECKPOINT_TAB.integratedCost} className="text-2xl font-heading font-bold text-charcoal mt-1">${(checkpoint.integrated_monthly_cost ?? 0).toLocaleString("es-MX")} / mes</p>
        <p className="text-xs text-charcoal/50 mt-1">Salario nominal: ${(checkpoint.monthly_salary ?? 0).toLocaleString("es-MX")} · {checkpoint.years_of_service} año(s) de antigüedad</p>
        {checkpoint.salario_minimo_outdated && (
          <div className="flex items-start gap-2 mt-3 bg-accent/40 border border-petroleum/10 rounded-lg p-2.5">
            <Info className="w-3.5 h-3.5 text-petroleum mt-0.5 shrink-0" strokeWidth={1.5} />
            <p className="text-xs text-charcoal/70">{checkpoint.salario_minimo_notice}</p>
          </div>
        )}
      </div>

      <div className="bg-card border border-petroleum/10 rounded-2xl p-5">
        <p className="font-heading font-semibold text-charcoal mb-1">Estimado de costo de un movimiento (baja)</p>
        <p className="text-xs text-charcoal/50 mb-3">Los conceptos que aplican dependen de por qué termina la relación laboral. Elige la causa antes de ver un estimado.</p>
        <label className="text-xs font-semibold text-charcoal/60 uppercase tracking-wide">Causa de terminación</label>
        <select
          data-testid={CHECKPOINT_TAB.causaSelect}
          value={causa}
          onChange={(e) => setCausa(e.target.value)}
          className="w-full mt-1 mb-4 border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm"
        >
          <option value="">Selecciona una causa...</option>
          {checkpoint.causas_disponibles?.map((c) => (
            <option key={c.id} value={c.id}>{c.label}{!c.auto_calculable ? " (revisión legal caso por caso)" : ""}</option>
          ))}
        </select>

        {causa && (
          <div data-testid={CHECKPOINT_TAB.causaDescripcion} className="flex items-start gap-2 bg-accent/40 border border-petroleum/10 rounded-lg p-2.5 mb-4">
            <Info className="w-3.5 h-3.5 text-petroleum mt-0.5 shrink-0" strokeWidth={1.5} />
            <p className="text-xs text-charcoal/70">
              {checkpoint.causas_disponibles?.find((c) => c.id === causa)?.descripcion}
            </p>
          </div>
        )}

        {!causa && (
          <p className="text-sm text-charcoal/50">Selecciona una causa de terminación para ver el estimado.</p>
        )}

        {causa && l && !l.auto_calculable && (
          <div data-testid={CHECKPOINT_TAB.notAutoCalculableNotice} className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3">
            <p className="text-xs text-amber-800">{l.notice}</p>
          </div>
        )}

        {causa && l && (
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold text-charcoal/60 uppercase tracking-wide mb-2">Finiquito (trabajo ya realizado)</p>
              <div className="space-y-2">
                {FINIQUITO_LABELS.map(([key, label]) => (
                  <div key={key} data-testid={CHECKPOINT_TAB.liquidationBreakdownRow} className="flex justify-between text-sm">
                    <span className="text-charcoal/70">{label} <span className="text-charcoal/40 text-xs">({l.finiquito[key].base})</span></span>
                    <span className="font-medium text-charcoal shrink-0 ml-3">${(l.finiquito[key]?.monto ?? 0).toLocaleString("es-MX")}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm font-semibold text-charcoal">
                  <span>Subtotal finiquito</span>
                  <span>${(l.finiquito_total ?? 0).toLocaleString("es-MX")}</span>
                </div>
              </div>
            </div>

            {l.indemnizacion && (
              <div>
                <p className="text-xs font-semibold text-charcoal/60 uppercase tracking-wide mb-2">Indemnización / prestaciones adicionales</p>
                <div className="space-y-2">
                  {INDEMNIZACION_LABELS.filter(([key]) => l.indemnizacion[key]).map(([key, label]) => (
                    <div key={key} data-testid={CHECKPOINT_TAB.liquidationBreakdownRow} className="flex justify-between text-sm">
                      <span className="text-charcoal/70">{label} <span className="text-charcoal/40 text-xs">({l.indemnizacion[key].base})</span></span>
                      <span className="font-medium text-charcoal shrink-0 ml-3">${(l.indemnizacion[key]?.monto ?? 0).toLocaleString("es-MX")}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm font-semibold text-charcoal">
                    <span>Subtotal indemnización</span>
                    <span>${(l.indemnizacion_total ?? 0).toLocaleString("es-MX")}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="border-t border-petroleum/10 pt-2 flex justify-between">
              <span className="font-semibold text-charcoal">Total estimado</span>
              <span data-testid={CHECKPOINT_TAB.liquidationTotal} className="font-heading font-bold text-lg text-petroleum">${(l.total_estimado ?? 0).toLocaleString("es-MX")}</span>
            </div>
          </div>
        )}

        {causa && l && (
          <div data-testid={CHECKPOINT_TAB.disclaimer} className="flex items-start gap-2 mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3">
            <ShieldAlert className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" strokeWidth={1.5} />
            <p className="text-xs text-amber-800">{l.disclaimer} Esta cifra nunca debe presentarse como final.</p>
          </div>
        )}
        {checkpoint.training_summary.count > 0 && (
          <div className="flex items-center justify-between gap-3 mt-4 bg-accent/40 border border-petroleum/10 rounded-xl p-3">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-petroleum shrink-0" strokeWidth={1.5} />
              <span className="text-sm text-charcoal/70">Ya invertido en su capacitación</span>
            </div>
            <span className="text-sm font-semibold text-charcoal shrink-0">
              <span data-testid={CHECKPOINT_TAB.trainingInvestmentCost}>${(checkpoint.training_summary?.total_cost ?? 0).toLocaleString("es-MX")}</span>
              {" · "}
              <span data-testid={CHECKPOINT_TAB.trainingInvestmentHours}>{(checkpoint.training_summary?.total_hours ?? 0).toLocaleString("es-MX")} h</span>
            </span>
          </div>
        )}
      </div>

      <div className="bg-card border border-petroleum/10 rounded-2xl p-5">
        <p className="font-heading font-semibold text-charcoal mb-3 flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Guía de conversación</p>
        <ul data-testid={CHECKPOINT_TAB.conversationGuide} className="space-y-2 text-sm text-charcoal/70 list-disc pl-4">
          {checkpoint.conversation_guide.map((tip, idx) => <li key={idx}>{tip}</li>)}
        </ul>
      </div>

      <ConsultingRequestButton
        employeeId={employeeId}
        defaultContext={`Quiero orientación sobre el caso de ${checkpoint.full_name} antes de tomar una decisión.`}
        label={`Solicitar consultoría sobre ${checkpoint.full_name}`}
      />
    </div>
  );
}
